import { formatDateToString } from 'shared/dateFns'
import logger from 'shared/logger'
import {
  reportTemplateRegistry,
  type ReportTemplate,
  GA4_MEASUREMENT_ID,
  type DashboardDataSource,
  type DashboardSheetDataSource,
  DEFAULT_CURRENCY_OPTIONS
} from './constants'
import { type StandardReportResp } from 'shared/api/standardReports'
import { STATUSES, type ConfigStatus } from 'shared/configStatuses'
import { getPlatformDetailsByPlatform } from 'src/dataSource/utils'
import { camelCaseToSnakeCase } from 'shared/utils/stringFormatter'
const log = logger()

export const parseReportTemplate = (reportTemplate: ReportTemplate): ReportTemplate => {
  if (reportTemplate.inputs === undefined) {
    return reportTemplate
  }

  // Add default currency options to all currency dropdowns
  Object.entries(reportTemplate.inputs).forEach(([inputId, inputs]) => {
    inputs.forEach((inputObject, idx) => {
      if (
        inputObject.id.indexOf('Currency') > 1 &&
        inputObject.type === 'dropdown' &&
        reportTemplate.inputs !== undefined
      ) {
        reportTemplate.inputs[inputId][idx].options = DEFAULT_CURRENCY_OPTIONS
      }
    })
  })

  return reportTemplate
}

export const getReportTemplateById = (reportId: string): ReportTemplate | undefined => {
  const reportTemplate: ReportTemplate | undefined = reportTemplateRegistry[reportId]
  if (reportTemplate === undefined) {
    log.error(`Report template with id ${reportId} not found`)
    return undefined
  }

  return parseReportTemplate(reportTemplate)
}

export const isReportTemplateScheduledForDeprecation = (reportId: string | undefined): boolean =>
  reportId != null && reportTemplateRegistry[reportId]?.deprecation_date != null

const formatToday = (): string => formatDateToString({ date: new Date(), targetFormat: 'yyyy-MM-dd' })

export const getDashboardLink = (
  ReportingSolutionConfig: StandardReportResp,
  sheetsLinks: string[]
): URL | undefined => {
  /**
   * Generates a link for the user to generate a dashboard using the Linking API: https://developers.google.com/datastudio/share/linking-api
   * On click it will use the dashboard information specified in the reportDefinition and
   * the actual config to make a custom link for the config.
   */
  const reportTemplate = getReportTemplateById(ReportingSolutionConfig.templateId)
  const dashboardInfo = reportTemplate?.dashboard

  // If it hasn't been defined return undefined
  if (dashboardInfo == null) {
    return undefined
  }

  const url = new URL('https://datastudio.google.com/reporting/create')
  url.searchParams.set('c.reportId', dashboardInfo.template_report_id)
  url.searchParams.set('c.mode', 'edit')

  const today = formatToday()
  url.searchParams.set('r.reportName', dashboardInfo.report_name_template.replace('{today}', today))
  url.searchParams.set('r.measurementId', GA4_MEASUREMENT_ID)

  // Loop through each datasource defined in the dashboard.
  dashboardInfo.datasources.forEach((datasource: DashboardDataSource) => {
    // Build a datasource name from the template provided in the JSON config.
    const datasourceName = getDatasourceName(
      ReportingSolutionConfig.name,
      reportTemplate?.id ?? '',
      datasource.datasourceName
    )
    url.searchParams.set(`ds.${datasource.alias}.datasourceName`, datasourceName)

    if (datasource.type === 'CUSTOM') {
      // For CUSTOM datasources, only process the provided sqlReplace property.
      if (datasource.sqlReplace !== undefined) {
        const replacements = {
          '{project_id}': ReportingSolutionConfig.targetProjectId.toString(),
          '{dataset_id}': ReportingSolutionConfig.targetDataset,
          '{name}': ReportingSolutionConfig.name.toLowerCase().replace(`${reportTemplate?.id ?? ''}_`, '')
        }

        // Replace tokens (e.g., {project_id}, {dataset_id}, {name}) in the SQL query.
        const sqlQuery = datasource.sqlReplace.replace(
          /\{(project_id|dataset_id|name)\}/g,
          (match) => replacements[match as keyof typeof replacements] ?? match
        )

        // Set the processed SQL query as a URL parameter.
        url.searchParams.set(`ds.${datasource.alias}.sqlReplace`, sqlQuery)

        // Set the billing project ID as a URL parameter.
        url.searchParams.set(
          `ds.${datasource.alias}.billingProjectId`,
          ReportingSolutionConfig.targetProjectId.toString()
        )
      }
    } else if (datasource.type === 'TABLE') {
      // For table-based datasources, tableId is mandatory.
      if (datasource.tableId === undefined) {
        log.error(`Table ID required for datasource ${datasource.alias}`)
      } else {
        // Set common BigQuery parameters.
        url.searchParams.set(`ds.${datasource.alias}.connector`, 'bigQuery')
        url.searchParams.set(`ds.${datasource.alias}.projectId`, ReportingSolutionConfig.targetProjectId.toString())
        url.searchParams.set(`ds.${datasource.alias}.datasetId`, ReportingSolutionConfig.targetDataset)
        url.searchParams.set(
          `ds.${datasource.alias}.tableId`,
          getDataSourceTableName(ReportingSolutionConfig.name, reportTemplate?.id ?? '', datasource.tableId)
        )

        if (datasource.type !== null) {
          url.searchParams.set(`ds.${datasource.alias}.type`, datasource.type)
        }
        if (datasource.isPartitioned !== undefined) {
          url.searchParams.set(`ds.${datasource.alias}.isPartitioned`, datasource.isPartitioned)
        }
        if (datasource.refreshFields !== undefined) {
          url.searchParams.set(`ds.${datasource.alias}.refreshFields`, datasource.refreshFields)
        }
      }
    } else {
      throw new Error(
        `Invalid datasource type: ${(datasource as DashboardDataSource).type} for datasource ${
          (datasource as DashboardDataSource).alias
        }`
      )
    }
  })

  // For each of the sheet datasources required for the report
  dashboardInfo.sheets_datasources?.forEach((sheetsDatasource: DashboardSheetDataSource, index) => {
    const sheetsLink = sheetsLinks[index]
    const match = /\/spreadsheets\/d\/([^/]+)\/edit#gid=([^/]+)/.exec(sheetsLink)

    const spreadsheetId = match?.[1]
    const worksheetId = match?.[2]
    if ([sheetsLink, spreadsheetId, worksheetId].includes(undefined)) {
      return
    }

    url.searchParams.set(`ds.${sheetsDatasource.alias}.connector`, 'googleSheets')
    const datasourceName = getDatasourceName(
      ReportingSolutionConfig.name,
      reportTemplate?.id ?? '',
      sheetsDatasource.datasourceName
    )
    url.searchParams.set(`ds.${sheetsDatasource.alias}.datasourceName`, datasourceName)
    url.searchParams.set(`ds.${sheetsDatasource.alias}.spreadsheetId`, spreadsheetId as string)
    url.searchParams.set(`ds.${sheetsDatasource.alias}.worksheetId`, worksheetId as string)
    url.searchParams.set(`ds.${sheetsDatasource.alias}.refreshFields`, sheetsDatasource.refreshFields)
  })

  return url
}

const getDatasourceName = (name: string, id: string, datasourceName: string): string => {
  const today = formatToday()
  return datasourceName.replace('{name}', name.toLowerCase().replace(`${id}_`, '')) + ` - ${today}`
}

export const getDataSourceTableName = (
  targetTableName: string,
  reportId: string,
  dataSourceTableId: string
): string => {
  return dataSourceTableId.replace('{name}', targetTableName.toLowerCase().replace(`${reportId}_`, ''))
}

export const getConnectedAccounts = (reportingSolutions: StandardReportResp): string[] => {
  return Object.entries(reportingSolutions.sources)
    .filter(([, sourceArray]) => sourceArray?.length > 0)
    .map(([sourceType]) => sourceType)
}

export const isPlatformKeyVisibleForUser = (platformKey: string, shouldShowInternalFeatures: boolean): boolean => {
  const details = getPlatformDetailsByPlatform(camelCaseToSnakeCase(platformKey))
  return !(details?.isInternalOnly && !shouldShowInternalFeatures)
}

/**
 * Checks if the report data loading view condition is satisfied.
 * This indicates when the report is still processing and data is not yet available.
 */
export const checkIfReportDataLoadingViewConditionSatisfied = ({
  status,
  isDataNotFoundError
}: {
  status: ConfigStatus | undefined
  isDataNotFoundError: boolean
}): boolean => isDataNotFoundError && status != null && (status === 'running' || status === STATUSES.pending)

/**
 * Checks if the report data unknown error view condition is satisfied.
 * This indicates when there's an unexpected error that isn't a known configuration error.
 */
export const checkIfReportDataUnknownErrorViewConditionSatisfied = ({
  isDataError,
  status,
  isReportDataLoadingViewConditionSatisfied
}: {
  isDataError: boolean
  status: ConfigStatus | undefined
  isReportDataLoadingViewConditionSatisfied: boolean
}): boolean => isDataError && status !== 'error' && !isReportDataLoadingViewConditionSatisfied

/**
 * Checks if the report data known error view condition is satisfied.
 * This indicates when there's a known configuration error with the report.
 */
export const checkIfReportDataKnownErrorViewConditionSatisfied = ({
  isDataNotFoundError,
  status
}: {
  isDataNotFoundError: boolean
  status: ConfigStatus | undefined
}): boolean => status === 'error' && isDataNotFoundError
